import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { FALLBACK_CURRENCY } from '../invoiceForm';
import { getCurrencies, getInvoiceSettings, updateInvoiceSettings } from '../services/invoiceService';
import { rememberTenantCurrency } from '../tenantCurrency';
import { CurrencyOption, InvoiceSettings } from '../types/Invoice';
import catalog from '../../catalog/Catalog.module.css';
import styles from '../Invoice.module.css';

export const InvoiceSettingsPage: React.FC = () => {
  const { t } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [settings, setSettings] = React.useState<InvoiceSettings | null>(null);
  const [currencies, setCurrencies] = React.useState<CurrencyOption[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({
    defaultCurrency: FALLBACK_CURRENCY,
    invoicePrefix: 'INV',
    creditPrefix: 'CN',
    defaultDueDays: '0',
    pricesIncludeTax: false,
    useDayView: false,
    roundingIncrement: '0.01',
  });
  const canUpdate = hasPermission(PermissionScopes.invoicesUpdate);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [data, currencyOptions] = await Promise.all([
          getInvoiceSettings(),
          getCurrencies().catch(() => [] as CurrencyOption[]),
        ]);
        setSettings(data);
        setCurrencies(currencyOptions);
        rememberTenantCurrency(data.defaultCurrency);
        setForm({
          defaultCurrency: data.defaultCurrency,
          invoicePrefix: data.invoicePrefix,
          creditPrefix: data.creditPrefix,
          defaultDueDays: String(data.defaultDueDays),
          pricesIncludeTax: data.pricesIncludeTax,
          useDayView: data.useDayView,
          roundingIncrement: String(data.roundingIncrement),
        });
      } catch (error) {
        setGeneralError(error instanceof Error ? error.message : t('failedToLoad', { ns: 'invoices' }));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [t]);

  const onSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setGeneralError(null);
    try {
      const updated = await updateInvoiceSettings({
        defaultCurrency: form.defaultCurrency,
        invoicePrefix: form.invoicePrefix.trim().toUpperCase(),
        creditPrefix: form.creditPrefix.trim().toUpperCase(),
        defaultDueDays: Number(form.defaultDueDays) || 0,
        pricesIncludeTax: form.pricesIncludeTax,
        useDayView: form.useDayView,
        roundingIncrement: Number(form.roundingIncrement) || 0.01,
      });
      setSettings(updated);
      rememberTenantCurrency(updated.defaultCurrency);
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToSave', { ns: 'invoices' }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loading', { ns: 'invoices' })} /></div>;
  }

  const currencyChoices = currencies.some(option => option.code === form.defaultCurrency)
    ? currencies
    : [{ code: form.defaultCurrency, name: form.defaultCurrency, symbol: '' }, ...currencies];

  return (
    <div className={`${catalog.page} ${styles.page}`}>
      <Breadcrumb items={[
        { label: t('title', { ns: 'invoices' }), path: '/invoices' },
        { label: t('settingsTitle', { ns: 'invoices' }), path: '/invoices/settings' },
      ]} />
      <h2 className={catalog.title}>{t('settingsTitle', { ns: 'invoices' })}</h2>
      {generalError ? <div className="error">{generalError}</div> : null}
      <form className={catalog.form} onSubmit={onSave}>
        <div className={catalog.field}>
          <label className={catalog.label}>{t('defaultCurrency', { ns: 'invoices' })}</label>
          <select
            className={catalog.input}
            value={form.defaultCurrency}
            disabled={!canUpdate}
            onChange={event => setForm(prev => ({ ...prev, defaultCurrency: event.target.value }))}
          >
            {currencyChoices.map(option => (
              <option key={option.code} value={option.code}>
                {option.symbol ? `${option.code} — ${option.name} (${option.symbol})` : `${option.code} — ${option.name}`}
              </option>
            ))}
          </select>
          <p className={catalog.detailValue}>{t('defaultCurrencyHint', { ns: 'invoices' })}</p>
        </div>
        <div className={catalog.field}>
          <label className={catalog.label}>{t('invoicePrefix', { ns: 'invoices' })}</label>
          <input className={catalog.input} value={form.invoicePrefix} disabled={!canUpdate} onChange={event => setForm(prev => ({ ...prev, invoicePrefix: event.target.value }))} />
        </div>
        <div className={catalog.field}>
          <label className={catalog.label}>{t('creditPrefix', { ns: 'invoices' })}</label>
          <input className={catalog.input} value={form.creditPrefix} disabled={!canUpdate} onChange={event => setForm(prev => ({ ...prev, creditPrefix: event.target.value }))} />
        </div>
        <div className={catalog.field}>
          <label className={catalog.label}>{t('defaultDueDays', { ns: 'invoices' })}</label>
          <input className={catalog.input} value={form.defaultDueDays} disabled={!canUpdate} onChange={event => setForm(prev => ({ ...prev, defaultDueDays: event.target.value }))} />
        </div>
        <div className={catalog.field}>
          <label className={catalog.label}>{t('roundingIncrement', { ns: 'invoices' })}</label>
          <input className={catalog.input} value={form.roundingIncrement} disabled={!canUpdate} onChange={event => setForm(prev => ({ ...prev, roundingIncrement: event.target.value }))} />
        </div>
        <div className={catalog.field}>
          <label className={catalog.label}>{t('pricesIncludeTax', { ns: 'invoices' })}</label>
          <label>
            <input type="checkbox" checked={form.pricesIncludeTax} disabled={!canUpdate} onChange={event => setForm(prev => ({ ...prev, pricesIncludeTax: event.target.checked }))} />
            {' '}{t('pricesIncludeTax', { ns: 'invoices' })}
          </label>
        </div>
        <div className={catalog.field}>
          <label className={catalog.label}>{t('useDayView', { ns: 'invoices' })}</label>
          <label>
            <input type="checkbox" checked={form.useDayView} disabled={!canUpdate} onChange={event => setForm(prev => ({ ...prev, useDayView: event.target.checked }))} />
            {' '}{t('useDayView', { ns: 'invoices' })}
          </label>
          <p className={catalog.detailValue}>{t('useDayViewHint', { ns: 'invoices' })}</p>
        </div>
        {settings ? (
          <>
            <div className={catalog.field}>
              <label className={catalog.label}>{t('nextInvoiceNumber', { ns: 'invoices' })}</label>
              <p className={catalog.detailValue}>{settings.invoiceNextNumber}</p>
            </div>
            <div className={catalog.field}>
              <label className={catalog.label}>{t('nextCreditNumber', { ns: 'invoices' })}</label>
              <p className={catalog.detailValue}>{settings.creditNextNumber}</p>
            </div>
          </>
        ) : null}
        <div className={catalog.actions}>
          <button className="btn btn-secondary" type="button" onClick={() => navigate('/invoices')}>{t('back', { ns: 'invoices' })}</button>
          {canUpdate ? <button className="btn btn-primary" type="submit" disabled={saving}>{t('save', { ns: 'invoices' })}</button> : null}
        </div>
      </form>
    </div>
  );
};

export default InvoiceSettingsPage;
